import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Phone, MessageCircle, Languages, MapPin, Cloud,
  Radar, ScanLine, Lock, BellRing, ShieldCheck, CheckCircle2, Truck, Sparkles,
  Plus, Minus, Activity, Clock, Globe, Wrench, Droplets, FileCheck, Briefcase,
  Send, Package, FileText, User, MoreHorizontal, Bell, ChevronRight, Camera,
  TrendingUp, Building2, Wallet, BadgeCheck, Menu
} from 'lucide-react';
import { MarketingMobileNavDrawer } from '../components/MarketingMobileNavDrawer';
import { toast } from '../hooks/use-toast';
import { faqsData } from '../data/mock';
import { translations } from '../i18n/translations';
import { getPublicApiBaseUrl } from '../utils/publicApi';
import { trackGoogleAdsFormConversion } from '../utils/googleAds';
import { BRAND_ICON_SRC } from '../constants/brand';

const HomePage = () => {
  const [language, setLanguage] = useState(() => localStorage.getItem('autogoLanguage') || 'es');
  const [formData, setFormData] = useState({
    pickupLocation: '', deliveryLocation: '', pickupDate: '', deliveryDate: '',
    vehicleType: '', name: '', email: '', phone: ''
  });
  const [openFaq, setOpenFaq] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const t = translations[language];
  const mobileNavLinks = [
    { href: '#tecnologia', label: t.nav.tech, testId: 'mnav-tech' },
    { href: '#servicios', label: t.nav.services, testId: 'mnav-services' },
    { to: '/recuperacion', label: t.nav.recovery, testId: 'mnav-recovery' },
    { to: '/flotas', label: t.nav.corporate, testId: 'mnav-corporate' },
    { href: '#como-funciona', label: t.nav.howItWorks, testId: 'mnav-how' },
    { href: '#faq', label: t.nav.faq, testId: 'mnav-faq' },
  ];
  const faqs = faqsData[language];
  const revealRefs = useRef([]);

  // Reveal-on-scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Sticky header style change
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'deliveryDate') {
      if (formData.pickupDate && value && value < formData.pickupDate) {
        toast({
          title: t.hero.dateOrderErrorTitle,
          description: t.hero.dateOrderErrorDescription,
          variant: 'destructive',
        });
        return;
      }
    }
    if (name === 'pickupDate') {
      setFormData((prev) => {
        const next = { ...prev, pickupDate: value };
        if (value && prev.deliveryDate && prev.deliveryDate < value) {
          next.deliveryDate = value;
        }
        return next;
      });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      formData.pickupDate &&
      formData.deliveryDate &&
      formData.deliveryDate < formData.pickupDate
    ) {
      toast({
        title: t.hero.dateOrderErrorTitle,
        description: t.hero.dateOrderErrorDescription,
        variant: 'destructive',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${getPublicApiBaseUrl()}/api/send-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Error');
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Quote Request',
          content_category: 'Vehicle Transport',
          value: 0, currency: 'EUR'
        });
      }
      trackGoogleAdsFormConversion();
      toast({ title: t.hero.successTitle, description: t.hero.successDescription });
      setFormData({
        pickupLocation: '', deliveryLocation: '', pickupDate: '', deliveryDate: '',
        vehicleType: '', name: '', email: '', phone: ''
      });
    } catch (error) {
      toast({ title: t.hero.errorTitle, description: t.hero.errorDescription, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = (withFormData = false) => {
    let message = '';
    if (withFormData && formData.pickupLocation && formData.deliveryLocation) {
      if (language === 'es') {
        message = `Hola! Solicito presupuesto para transporte de vehículo:\n\n` +
          `📍 Recogida: ${formData.pickupLocation}\n` +
          `📍 Entrega: ${formData.deliveryLocation}\n` +
          `📅 Fecha recogida: ${formData.pickupDate || 'A confirmar'}\n` +
          `📅 Fecha entrega: ${formData.deliveryDate || 'A confirmar'}\n` +
          `🚗 Vehículo: ${formData.vehicleType || 'No especificado'}\n\n` +
          `👤 ${formData.name || ''}\n📧 ${formData.email || ''}\n📱 ${formData.phone || ''}`;
      } else {
        message = `Hello! I request a vehicle transport quote:\n\n` +
          `📍 Pickup: ${formData.pickupLocation}\n` +
          `📍 Delivery: ${formData.deliveryLocation}\n` +
          `📅 Pickup: ${formData.pickupDate || 'TBD'}\n` +
          `📅 Delivery: ${formData.deliveryDate || 'TBD'}\n` +
          `🚗 Vehicle: ${formData.vehicleType || 'N/A'}\n\n` +
          `👤 ${formData.name || ''}\n📧 ${formData.email || ''}\n📱 ${formData.phone || ''}`;
      }
    } else {
      message = language === 'es'
        ? 'Hola, me gustaría información sobre el transporte de vehículos AutoGo'
        : 'Hello, I would like information about AutoGo vehicle transport';
    }
    if (window.fbq) {
      window.fbq('track', 'Contact', { content_name: 'WhatsApp Click', content_category: 'Customer Support' });
    }
    window.open(`https://wa.me/34930491501?text=${encodeURIComponent(message)}`, '_blank');
  };

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === 'es' ? 'en' : 'es';
      localStorage.setItem('autogoLanguage', next);
      return next;
    });
  };

  return (
    <div className="relative min-h-screen text-[#F8FAFC] overflow-x-hidden" style={{ background: '#060B14' }}>
      {/* Ambient background */}
      <div className="ag-grain" />
      <div className="fixed inset-0 ag-grid-bg opacity-[0.35] pointer-events-none" />
      <div className="ag-radial-spot" style={{ top: '-200px', left: '-150px', width: '600px', height: '600px', background: 'rgba(0, 242, 96, 0.18)' }} />
      <div className="ag-radial-spot" style={{ top: '20%', right: '-200px', width: '700px', height: '700px', background: 'rgba(27, 147, 164, 0.18)' }} />
      <div className="ag-radial-spot" style={{ bottom: '10%', left: '20%', width: '500px', height: '500px', background: 'rgba(0, 242, 96, 0.07)' }} />

      {/* ============= Top stack: safe area + banner + header ============= */}
      <div
        className="fixed top-0 inset-x-0 z-50 flex flex-col"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        data-testid="marketing-top-stack"
      >
        <div
          className="ag-glass-strong shrink-0 border-b border-white/5 py-2 text-center text-[11px] sm:text-xs font-mono uppercase tracking-[0.18em]"
          data-testid="top-banner"
        >
          <a
            href="#recuperacion"
            className="inline-flex max-w-[100vw] items-center gap-2 px-2 text-[#CBD5E1] hover:text-[#00F260] transition-colors"
          >
            <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-[#00F260] ag-pulse" />
            <span className="truncate">{t.topBanner}</span>
            <ArrowRight className="h-3 w-3 shrink-0" />
          </a>
        </div>

        <header
          className={`shrink-0 transition-all duration-300 ag-glass-strong ${scrolled ? 'border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.35)]' : 'border-b border-white/5'}`}
          data-testid="main-header"
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-4 md:h-20 md:px-10">
            <a href="#top" className="group flex min-w-0 shrink-0 items-center gap-2 sm:gap-3" data-testid="logo-link">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 ag-glass-strong sm:h-10 sm:w-10">
                <img src={BRAND_ICON_SRC} alt="AutoGo" className="h-6 w-6 object-contain sm:h-7 sm:w-7" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#00F260]/20 to-transparent" />
              </div>
              <div className="min-w-0 leading-none">
                <div className="font-display text-base font-extrabold tracking-tight sm:text-lg md:text-xl">AUTOGO</div>
              </div>
            </a>

            <nav className="hidden items-center gap-7 text-sm text-[#94A3B8] xl:flex">
              <a href="#tecnologia" className="transition-colors hover:text-white" data-testid="nav-tech">
                {t.nav.tech}
              </a>
              <a href="#servicios" className="transition-colors hover:text-white" data-testid="nav-services">
                {t.nav.services}
              </a>
              <Link to="/recuperacion" className="transition-colors hover:text-white" data-testid="nav-recovery">
                {t.nav.recovery}
              </Link>
              <Link to="/flotas" className="transition-colors hover:text-white" data-testid="nav-corporate">
                {t.nav.corporate}
              </Link>
              <a href="#como-funciona" className="transition-colors hover:text-white" data-testid="nav-how">
                {t.nav.howItWorks}
              </a>
              <a href="#faq" className="transition-colors hover:text-white" data-testid="nav-faq">
                {t.nav.faq}
              </a>
            </nav>

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 text-white/90 hover:border-[#00F260]/50 hover:bg-white/[0.06] xl:hidden"
                aria-label={language === 'es' ? 'Abrir menú' : 'Open menu'}
                aria-expanded={mobileNavOpen}
                data-testid="mobile-menu-open"
              >
                <Menu className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={toggleLanguage}
                className="hidden items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#94A3B8] transition-all hover:border-[#00F260]/40 hover:text-white sm:flex"
                aria-label="Change language"
                data-testid="language-toggle"
              >
                <Languages className="h-3.5 w-3.5" />
                {language}
              </button>
              <Link
                to="/login"
                className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-white transition-colors hover:border-[#00F260] hover:text-[#00F260] sm:inline-flex sm:text-sm"
                data-testid="header-login-btn"
              >
                <User className="h-3.5 w-3.5" />
                {t.nav.login}
              </Link>
              <a
                href="#hero"
                className="group relative inline-flex items-center gap-1.5 rounded-full bg-[#00F260] px-3 py-2 text-xs font-semibold text-[#060B14] transition-colors hover:bg-white sm:gap-2 sm:px-5 sm:py-2.5 sm:text-sm"
                data-testid="header-cta-quote"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0" />
                <span className="sm:hidden">{language === 'es' ? 'Cotizar' : 'Quote'}</span>
                <span className="hidden sm:inline">{t.nav.callNow}</span>
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>
        </header>
      </div>

      <MarketingMobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        links={mobileNavLinks}
        language={language}
        onToggleLanguage={toggleLanguage}
        loginLabel={t.nav.login}
        loginTo="/login"
        partnersTitle={t.clients.tagline}
      />

      {/* ============= Hero ============= */}
      <section
        id="hero"
        className="relative px-5 pb-20 pt-[calc(140px+env(safe-area-inset-top,0px))] md:px-10 md:pb-32 md:pt-[calc(170px+env(safe-area-inset-top,0px))]"
        data-testid="hero-section"
      >
        <div id="top" />
        <div className="relative max-w-7xl mx-auto z-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left column */}
          <div className="lg:col-span-7 ag-animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 ag-glass rounded-full text-[10px] md:text-[11px] tracking-[0.25em] font-mono text-[#00F260] mb-6">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#00F260] animate-pulse" />
              {t.hero.tagline}
            </div>
            <h1 className="font-display text-[44px] sm:text-6xl lg:text-[80px] xl:text-[92px] font-extrabold leading-[0.95] tracking-tight">
              {t.hero.title}<br />
              <span className="ag-shine-text">{t.hero.titleHighlight}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-[#94A3B8] leading-relaxed">
              {t.hero.description}
            </p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              {[t.hero.verified, t.hero.insured, t.hero.delivery].map((label, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 ag-glass rounded-full text-xs text-[#94A3B8]" data-testid={`hero-badge-${i}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#00F260]" />
                  {label}
                </span>
              ))}
            </div>

            {/* Floating mockup — visible on lg+ */}
            <div className="hidden lg:flex mt-12 gap-4 ag-animate-fade-up" style={{ animationDelay: '.2s' }}>
              <TrackingMockup t={t} />
              <WeatherMockup t={t} />
            </div>
          </div>

          {/* Right column — Quote form */}
          <div className="lg:col-span-5 ag-animate-fade-up" style={{ animationDelay: '.15s' }}>
            <div className="ag-glass-strong rounded-3xl p-6 md:p-8 relative overflow-hidden" data-testid="quote-form-card">
              <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#00F260]/10 blur-3xl pointer-events-none" />
              <div className="flex items-center justify-between mb-5 relative z-10">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-[#00F260] mb-1.5">QUICK QUOTE</div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold">{t.hero.formTitle}</h3>
                  <p className="text-xs text-[#94A3B8] mt-1.5">{t.hero.formSubtitle}</p>
                </div>
                <Send className="w-6 h-6 text-[#00F260]" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 relative z-10" data-testid="quote-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.pickupLocation}</label>
                    <input className="ag-input" name="pickupLocation" value={formData.pickupLocation} onChange={handleChange} placeholder={t.hero.pickupPlaceholder} required data-testid="input-pickup-location" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.deliveryLocation}</label>
                    <input className="ag-input" name="deliveryLocation" value={formData.deliveryLocation} onChange={handleChange} placeholder={t.hero.deliveryPlaceholder} required data-testid="input-delivery-location" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.pickupDate}</label>
                    <input
                      className="ag-input"
                      type="date"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleChange}
                      max={formData.deliveryDate || undefined}
                      required
                      data-testid="input-pickup-date"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.deliveryDate}</label>
                    <input
                      className="ag-input"
                      type="date"
                      name="deliveryDate"
                      value={formData.deliveryDate}
                      onChange={handleChange}
                      min={formData.pickupDate || undefined}
                      required
                      data-testid="input-delivery-date"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.vehicleType}</label>
                  <input className="ag-input" name="vehicleType" value={formData.vehicleType} onChange={handleChange} placeholder={t.hero.vehiclePlaceholder} required data-testid="input-vehicle-type" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.name}</label>
                    <input className="ag-input" name="name" value={formData.name} onChange={handleChange} placeholder={t.hero.namePlaceholder} required data-testid="input-name" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.phone}</label>
                    <input className="ag-input" name="phone" value={formData.phone} onChange={handleChange} placeholder={t.hero.phonePlaceholder} required data-testid="input-phone" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{t.hero.email}</label>
                  <input className="ag-input" type="email" name="email" value={formData.email} onChange={handleChange} placeholder={t.hero.emailPlaceholder} required data-testid="input-email" />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#00F260] hover:bg-white text-[#060B14] font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60"
                  data-testid="submit-quote-btn"
                >
                  {isSubmitting ? '...' : t.hero.submit}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
                <button
                  type="button"
                  onClick={() => handleWhatsApp(true)}
                  className="w-full inline-flex items-center justify-center gap-2 border border-white/15 text-white hover:border-[#00F260] hover:text-[#00F260] py-3 rounded-full text-sm transition-colors"
                  data-testid="whatsapp-quote-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t.hero.whatsappBtn}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ============= Stats Strip ============= */}
      <section className="relative z-10 border-y border-white/10 ag-glass" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {[
            { v: t.stats.vehicles, l: t.stats.vehiclesLabel },
            { v: t.stats.satisfaction, l: t.stats.satisfactionLabel },
            { v: t.stats.delivery, l: t.stats.deliveryLabel },
            { v: t.stats.coverage, l: t.stats.coverageLabel, flag: 'eu' },
          ].map((s, i) => (
            <div key={i} className="px-4 md:px-8 py-7 md:py-10" data-testid={`stat-${i}`}>
              <div className="font-display text-3xl md:text-5xl font-extrabold text-white flex items-center gap-2 md:gap-3">
                {s.v}
                {s.flag === 'eu' && <EUFlag />}
              </div>
              <div className="mt-1 text-xs md:text-sm text-[#94A3B8]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============= Quick Access Strip ============= */}
      <QuickAccessStrip t={t} addReveal={addReveal} />

      {/* ============= Clients Marquee ============= */}
      <ClientsMarquee t={t} />

      {/* ============= Differentiators / Tecnología (Bento) ============= */}
      <section id="tecnologia" className="relative pt-12 pb-12 md:pt-16 md:pb-16 px-5 md:px-10" data-testid="tech-section">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-14 md:mb-20">
            <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{t.differentiators.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight">
              {t.differentiators.title}<br />
              <span className="text-[#94A3B8]">{t.differentiators.titleAccent}</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#94A3B8] max-w-xl">{t.differentiators.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
            {/* SmartTrack 360 */}
            <div ref={addReveal} className="ag-reveal ag-card ag-hover-lift md:col-span-7 p-7 md:p-10 relative overflow-hidden min-h-[380px]" data-testid="pillar-track">
              <PillarHeader Icon={Radar} tag={t.differentiators.pillars.track.tag} />
              <h3 className="font-display text-3xl md:text-5xl font-bold mt-5">{t.differentiators.pillars.track.name}</h3>
              <p className="mt-3 text-[#94A3B8] max-w-md">{t.differentiators.pillars.track.desc}</p>
              <BigTrackingVisual t={t} />
            </div>

            {/* Inspección 360° */}
            <div ref={addReveal} className="ag-reveal ag-card ag-hover-lift md:col-span-5 p-7 md:p-9 relative overflow-hidden min-h-[280px]" data-testid="pillar-check">
              <PillarHeader Icon={ScanLine} tag={t.differentiators.pillars.check.tag} />
              <h3 className="font-display text-2xl md:text-3xl font-bold mt-5">{t.differentiators.pillars.check.name}</h3>
              <p className="mt-3 text-[#94A3B8] text-sm md:text-base">{t.differentiators.pillars.check.desc}</p>
              <InspectionVisual t={t} />
            </div>

            {/* Panel Cliente — NEW */}
            <div ref={addReveal} className="ag-reveal ag-card ag-hover-lift md:col-span-7 p-7 md:p-9 relative overflow-hidden" data-testid="pillar-panel">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div>
                  <PillarHeader Icon={Activity} tag={t.differentiators.pillars.panel.tag} />
                  <h3 className="font-display text-2xl md:text-3xl font-bold mt-5">{t.differentiators.pillars.panel.name}</h3>
                  <p className="mt-3 text-[#94A3B8] text-sm md:text-base">{t.differentiators.pillars.panel.desc}</p>
                </div>
                <div className="flex justify-center md:justify-end">
                  <ClientDashboardMockup t={t} language={language} />
                </div>
              </div>
            </div>

            {/* SecurePIN */}
            <div ref={addReveal} className="ag-reveal ag-card ag-hover-lift md:col-span-5 p-7 md:p-9 relative overflow-hidden min-h-[260px]" data-testid="pillar-pin">
              <PillarHeader Icon={Lock} tag={t.differentiators.pillars.pin.tag} />
              <h3 className="font-display text-2xl md:text-3xl font-bold mt-5">{t.differentiators.pillars.pin.name}</h3>
              <p className="mt-3 text-[#94A3B8] text-sm md:text-base">{t.differentiators.pillars.pin.desc}</p>
              <PinVisual t={t} />
            </div>

            {/* Alertas */}
            <div ref={addReveal} className="ag-reveal ag-card ag-hover-lift md:col-span-12 p-7 md:p-10 relative overflow-hidden" data-testid="pillar-alerts">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <PillarHeader Icon={BellRing} tag={t.differentiators.pillars.alerts.tag} />
                  <h3 className="font-display text-3xl md:text-5xl font-bold mt-5">{t.differentiators.pillars.alerts.name}</h3>
                  <p className="mt-3 text-[#94A3B8] max-w-md">{t.differentiators.pillars.alerts.desc}</p>
                </div>
                <AlertsVisual language={language} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= Services (asymmetric massive type) ============= */}
      <section id="servicios" className="relative pt-12 md:pt-16 pb-12 md:pb-16 px-5 md:px-10 border-t border-white/5" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal mb-14 md:mb-20 grid md:grid-cols-12 items-end gap-6">
            <div className="md:col-span-7">
              <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{t.services.tagline}</div>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">{t.services.title}</h2>
            </div>
          </div>

          <div className="border-t border-white/10">
            {t.services.list.map((s, i) => {
              // 0=National, 1=International → quote form (#hero)
              // 2=Fleet → /flotas landing
              // 3=Premium → additional services section
              const links = ['#hero', '#hero', '/flotas', '#servicios-adicionales'];
              return (
                <ServiceRow key={i} index={i} title={s.title} desc={s.desc} to={links[i]} addReveal={addReveal} />
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= How it works ============= */}
      <section id="como-funciona" className="relative pt-12 md:pt-16 pb-24 md:pb-32 px-5 md:px-10" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal mb-10 md:mb-14 grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{t.howItWorks.tagline}</div>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
                {t.howItWorks.title} <span className="text-[#94A3B8]">{t.howItWorks.titleAccent}</span>
              </h2>
            </div>
            <p className="md:col-span-5 text-[#94A3B8] text-sm md:text-base md:pb-2">{t.howItWorks.subtitle}</p>
          </div>

          <div className="relative">
            {/* Connecting line desktop */}
            <div className="hidden md:block absolute top-[78px] left-[12%] right-[12%] h-px">
              <div className="w-full h-full bg-gradient-to-r from-transparent via-[#00F260]/30 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
              {t.howItWorks.steps.map((step, i) => {
                const Icon = [Send, ScanLine, Radar, Lock][i];
                return (
                  <div ref={addReveal} key={i} className="ag-reveal" style={{ transitionDelay: `${i * 90}ms` }} data-testid={`step-${i}`}>
                    {/* Icon node on timeline */}
                    <div className="hidden md:flex relative justify-center mb-6">
                      <div className="absolute inset-x-0 top-1/2 h-px bg-[#00F260]/15 -z-10" />
                      <div className="relative w-14 h-14 rounded-2xl ag-glass-strong border border-[#00F260]/30 flex items-center justify-center group-hover:border-[#00F260] transition-colors">
                        <Icon className="w-6 h-6 text-[#00F260]" strokeWidth={2} />
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#00F260] flex items-center justify-center font-mono text-[9px] font-bold text-[#060B14]">
                          {i + 1}
                        </span>
                      </div>
                    </div>

                    <div className="ag-card p-5 md:p-6 ag-hover-lift relative overflow-hidden h-full">
                      {/* Watermark step number */}
                      <span aria-hidden className="absolute -top-3 -right-1 font-display text-[80px] md:text-[110px] font-extrabold leading-none text-white/[0.04] select-none pointer-events-none">
                        {step.num}
                      </span>

                      {/* Mobile icon (no timeline) */}
                      <div className="md:hidden flex items-center gap-3 mb-3 relative z-10">
                        <div className="w-10 h-10 rounded-xl bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#00F260]" />
                        </div>
                        <span className="font-mono text-xs tracking-[0.25em] text-[#00F260]">{step.num}</span>
                      </div>

                      <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[#00F260] mb-2 relative z-10">{step.num}</div>
                      <h3 className="font-display text-xl md:text-2xl font-bold relative z-10">{step.title}</h3>
                      <p className="mt-2 text-sm text-[#94A3B8] relative z-10">{step.desc}</p>

                      {/* Bullets */}
                      <ul className="mt-4 space-y-1.5 relative z-10">
                        {step.bullets.map((b, j) => (
                          <li key={j} className="flex items-start gap-2 text-xs text-[#94A3B8]">
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#00F260] mt-0.5 shrink-0" strokeWidth={2.5} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============= Operating Margin (B2B premium teaser) ============= */}
      <OperatingMarginSection t={t} addReveal={addReveal} language={language} />

      {/* ============= Recovery ============= */}
      <section id="recuperacion" className="relative pt-12 md:pt-16 pb-12 md:pb-16 px-5 md:px-10" data-testid="recovery-section">
        <div className="max-w-7xl mx-auto relative">
          <div ref={addReveal} className="ag-reveal relative overflow-hidden ag-glass-strong rounded-[28px] p-8 md:p-14">
            <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-[#1B93A4]/15 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-[#00F260]/10 blur-3xl pointer-events-none" />

            <div className="relative grid lg:grid-cols-2 gap-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00F260]/10 border border-[#00F260]/30 rounded-full text-[10px] tracking-[0.25em] font-mono text-[#00F260] mb-6">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00F260] ag-pulse" />
                  {t.recovery.badge}
                </div>
                <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
                  {t.recovery.title}<br />
                  <span className="ag-shine-text">{t.recovery.titleAccent}</span>
                </h2>
                <p className="mt-5 text-[#94A3B8] max-w-lg">{t.recovery.description}</p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/recuperacion" className="group inline-flex items-center gap-2 bg-[#00F260] text-[#060B14] font-semibold px-5 py-3 rounded-full hover:bg-white transition-colors" data-testid="recovery-cta-1">
                    <ArrowUpRight className="w-4 h-4" />
                    {language === 'es' ? 'Ver división Recovery' : 'See Recovery division'}
                  </Link>
                  <button onClick={() => handleWhatsApp(false)} className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-3 rounded-full hover:border-[#00F260] hover:text-[#00F260] transition-colors" data-testid="recovery-cta-2">
                    <MessageCircle className="w-4 h-4" />
                    {t.recovery.btn2}
                  </button>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                  {t.recovery.stats.map((s, i) => (
                    <div key={i} className="border-l border-[#00F260]/40 pl-3">
                      <div className="font-display text-2xl font-bold text-white">{s.v}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {t.recovery.points.map((p, i) => (
                  <div key={i} className="ag-card p-5 md:p-6 flex items-start gap-4" data-testid={`recovery-point-${i}`}>
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-[#00F260]" />
                    </div>
                    <div>
                      <div className="font-display text-lg font-bold">{p.title}</div>
                      <div className="text-sm text-[#94A3B8] mt-1">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= Additional services ============= */}
      <section id="servicios-adicionales" className="relative pt-12 md:pt-16 pb-12 md:pb-16 px-5 md:px-10 border-t border-white/5" data-testid="additional-section">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal mb-14 md:mb-20 max-w-3xl">
            <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{t.additional.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">{t.additional.title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.additional.list.map((item, i) => {
              const Icon = [FileCheck, Droplets, Wrench, Briefcase][i];
              return (
                <div ref={addReveal} key={i} className="ag-reveal ag-card ag-hover-lift p-6" data-testid={`additional-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#00F260]" />
                  </div>
                  <h3 className="font-display text-lg font-bold">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#94A3B8]">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============= FAQ ============= */}
      <section id="faq" className="relative pt-12 md:pt-16 pb-12 md:pb-16 px-5 md:px-10" data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <div ref={addReveal} className="ag-reveal text-center mb-12 md:mb-16">
            <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{t.faq.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">{t.faq.title}</h2>
            <p className="mt-4 text-[#94A3B8] max-w-xl mx-auto">{t.faq.subtitle}</p>
          </div>

          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={f.id} className={`ag-card overflow-hidden transition-all ${openFaq === i ? 'border-[#00F260]/40' : ''}`} data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-base md:text-lg font-semibold">{f.question}</span>
                  <span className={`shrink-0 w-9 h-9 rounded-full border ${openFaq === i ? 'border-[#00F260] bg-[#00F260]/10 text-[#00F260]' : 'border-white/15 text-[#94A3B8]'} flex items-center justify-center transition-all`}>
                    {openFaq === i ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <div className={`grid transition-[grid-template-rows] duration-500 ease-out ${openFaq === i ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="overflow-hidden">
                    <p className="px-5 md:px-7 pb-6 text-sm md:text-base text-[#94A3B8] leading-relaxed">{f.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div ref={addReveal} className="ag-reveal mt-10 text-center">
            <button onClick={() => handleWhatsApp(false)} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00F260] hover:text-[#00F260] text-white px-6 py-3 rounded-full transition-colors" data-testid="faq-contact-btn">
              <MessageCircle className="w-4 h-4" />
              {t.faq.contactBtn}
            </button>
          </div>
        </div>
      </section>

      {/* ============= Footer ============= */}
      <footer id="contacto" className="relative pt-12 md:pt-20 pb-10 px-5 md:px-10 border-t border-white/5 overflow-hidden" data-testid="footer">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl ag-glass-strong overflow-hidden flex items-center justify-center">
                  <img src={BRAND_ICON_SRC} alt="AutoGo" className="w-7 h-7" />
                </div>
                <div className="leading-none">
                  <div className="font-display text-xl font-extrabold">AUTOGO</div>
                </div>
              </div>
              <p className="text-[#94A3B8] max-w-sm text-sm">{t.footer.description}</p>
              <button onClick={() => handleWhatsApp(false)} className="mt-6 inline-flex items-center gap-2 bg-[#00F260] text-[#060B14] font-semibold px-5 py-2.5 rounded-full hover:bg-white transition-colors text-sm" data-testid="footer-whatsapp-cta">
                <MessageCircle className="w-4 h-4" />
                {t.footer.whatsapp}
              </button>
            </div>

            <div className="md:col-span-3">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.quickLinks}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><a href="#tecnologia" className="hover:text-white transition-colors">{t.nav.tech}</a></li>
                <li><a href="#servicios" className="hover:text-white transition-colors">{t.nav.services}</a></li>
                <li><a href="#recuperacion" className="hover:text-white transition-colors">{t.nav.recovery}</a></li>
                <li><a href="#como-funciona" className="hover:text-white transition-colors">{t.nav.howItWorks}</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">{t.nav.faq}</a></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.contact}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><a href="tel:+34930491501" className="hover:text-white">+34 930 49 15 01</a></li>
                <li><a href="mailto:hello@autogodelivery.com" className="hover:text-white break-all">hello@autogodelivery.com</a></li>
                <li>Carrer de Lepant 270, 08013, Barcelona, España</li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.legal}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><Link to="/politica-de-privacidad" className="hover:text-white">{t.footer.privacyPolicy}</Link></li>
                <li><Link to="/politica-de-cookies" className="hover:text-white">{t.footer.cookiesPolicy}</Link></li>
              </ul>
            </div>
          </div>

          {/* Massive watermark + bottom */}
          <div className="relative pt-8 border-t border-white/5">
            <div aria-hidden className="font-display font-extrabold tracking-tight text-[18vw] leading-none text-white/[0.03] select-none pointer-events-none whitespace-nowrap text-center -mb-12 md:-mb-20">
              AUTOGO
            </div>
            <div className="relative flex flex-col items-center justify-center text-xs text-[#475569] z-10 text-center">
              <div>{t.footer.rights}</div>
            </div>
          </div>
        </div>
      </footer>

      {/* ============= Floating WhatsApp ============= */}
      <button
        onClick={() => handleWhatsApp(false)}
        className="fixed z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-2xl shadow-[#25D366]/40 ag-pulse transition-transform hover:scale-110 max-sm:right-4 max-sm:bottom-[max(1.25rem,env(safe-area-inset-bottom,0px))] sm:bottom-5 sm:right-5 md:bottom-8 md:right-8 md:h-16 md:w-16"
        aria-label="WhatsApp"
        data-testid="floating-whatsapp"
      >
        <MessageCircle className="w-6 h-6 md:w-7 md:h-7" />
      </button>
    </div>
  );
};

/* ====================================================
   Sub-components (kept in same file for cohesion)
   ==================================================== */

const PillarHeader = ({ Icon, tag }) => (
  <div className="flex items-center gap-3">
    <div className="w-11 h-11 rounded-xl bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#00F260]" />
    </div>
    <span className="font-mono text-[10px] tracking-[0.25em] text-[#00F260]">{tag}</span>
  </div>
);

/* EU flag — refined minimalist mark (just the star ring) */
const EUFlag = () => {
  const stars = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const r = 22;
    const cx = 28 + r * Math.cos(angle);
    const cy = 28 + r * Math.sin(angle);
    return { cx, cy };
  });
  return (
    <svg viewBox="0 0 56 56" className="w-10 h-10 md:w-14 md:h-14 shrink-0" aria-label="European Union" role="img">
      <defs>
        <symbol id="eu-star" viewBox="-1 -1 2 2" overflow="visible">
          <polygon points="0,-1 0.225,-0.309 0.951,-0.309 0.363,0.118 0.588,0.809 0,0.382 -0.588,0.809 -0.363,0.118 -0.951,-0.309 -0.225,-0.309" fill="#FFFFFF" />
        </symbol>
        <radialGradient id="eu-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00F260" stopOpacity="0.12" />
          <stop offset="70%" stopColor="#00F260" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="28" cy="28" r="26" fill="url(#eu-glow)" />
      {stars.map((s, i) => (
        <use key={i} href="#eu-star" x={s.cx - 3.2} y={s.cy - 3.2} width="6.4" height="6.4" />
      ))}
    </svg>
  );
};

/* Hero side mockups */
const TrackingMockup = ({ t }) => (
  <div className="ag-glass-strong rounded-2xl p-4 w-[260px]" data-testid="hero-tracking-mockup">
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-[10px] tracking-[0.25em] text-[#00F260]">{t.miniMockup.live}</span>
      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#00F260] animate-pulse" />
    </div>
    <div className="text-xs text-[#94A3B8]">{t.miniMockup.route}</div>
    <div className="font-display text-base font-bold mt-0.5">
      {t.miniMockup.city1} <span className="text-[#475569]">→</span> {t.miniMockup.city2}
    </div>
    <MiniRouteSVG />
    <div className="flex items-center justify-between mt-3 text-xs">
      <div>
        <div className="text-[#475569] text-[10px] uppercase tracking-widest">{t.miniMockup.eta}</div>
        <div className="font-mono text-white text-base">{t.miniMockup.etaTime}</div>
      </div>
      <div className="text-right">
        <div className="text-[#475569] text-[10px] uppercase tracking-widest flex items-center gap-1 justify-end"><Cloud className="w-3 h-3" /></div>
        <div className="font-mono text-white text-xs">{t.miniMockup.weather}</div>
      </div>
    </div>
  </div>
);

const WeatherMockup = ({ t }) => (
  <div className="ag-glass-strong rounded-2xl p-4 w-[180px] ag-animate-float" data-testid="hero-weather-mockup">
    <div className="font-mono text-[10px] tracking-[0.25em] text-[#00F260] mb-3">SECUREPIN</div>
    <div className="flex justify-between gap-1.5 mb-3">
      {['7', '4', '2', '9'].map((n, i) => (
        <div key={i} className="flex-1 aspect-square rounded-md ag-glass border border-[#00F260]/20 flex items-center justify-center font-mono text-lg font-bold text-[#00F260]">
          {n}
        </div>
      ))}
    </div>
    <div className="flex items-center gap-1.5 text-[10px] text-[#94A3B8]">
      <Lock className="w-3 h-3 text-[#00F260]" />
      Verified
    </div>
  </div>
);

const MiniRouteSVG = () => (
  <svg viewBox="0 0 220 60" className="w-full h-12 mt-2">
    <defs>
      <linearGradient id="rg" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#00F260" stopOpacity="0.2" />
        <stop offset="50%" stopColor="#00F260" stopOpacity="1" />
        <stop offset="100%" stopColor="#1B93A4" stopOpacity="0.6" />
      </linearGradient>
    </defs>
    <path d="M 10 40 Q 60 5 110 30 T 210 18" stroke="url(#rg)" strokeWidth="2" fill="none" strokeDasharray="4 4" style={{ animation: 'ag-route-dash 1.6s linear infinite' }} />
    <circle cx="10" cy="40" r="3.5" fill="#00F260" />
    <circle cx="210" cy="18" r="3.5" fill="#00F260" />
    <circle cx="115" cy="26" r="4" fill="#00F260">
      <animate attributeName="r" values="4;7;4" dur="1.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const BigTrackingVisual = ({ t }) => (
  <div className="mt-7 relative ag-glass rounded-xl p-5 border border-white/5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-2 w-2 rounded-full bg-[#00F260] animate-pulse" />
        <span className="font-mono text-[10px] tracking-[0.25em] text-[#00F260]">{t.miniMockup.live}</span>
      </div>
      <span className="font-mono text-[10px] text-[#94A3B8]">SAT-04 · 14:23 UTC</span>
    </div>
    <div className="flex items-center justify-between mb-2 text-xs">
      <div className="flex items-center gap-2 text-white">
        <MapPin className="w-3.5 h-3.5 text-[#00F260]" />
        <span className="font-display text-sm font-bold">{t.miniMockup.city1}</span>
      </div>
      <div className="flex items-center gap-2 text-white">
        <span className="font-display text-sm font-bold">{t.miniMockup.city2}</span>
        <MapPin className="w-3.5 h-3.5 text-[#00F260]" />
      </div>
    </div>
    <svg viewBox="0 0 600 140" className="w-full h-28">
      <defs>
        <linearGradient id="bgr" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#00F260" stopOpacity="0.15" />
          <stop offset="55%" stopColor="#00F260" stopOpacity="1" />
          <stop offset="100%" stopColor="#1B93A4" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      {/* Ghost grid */}
      {[...Array(7)].map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 20} x2="600" y2={i * 20} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      ))}
      {[...Array(13)].map((_, i) => (
        <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="140" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      ))}
      {/* Route */}
      <path d="M 25 100 Q 120 25 240 75 T 480 40 T 575 35" stroke="url(#bgr)" strokeWidth="3" fill="none" strokeDasharray="600" style={{ animation: 'ag-route-fill 5s ease forwards infinite' }} />
      <circle cx="25" cy="100" r="6" fill="#00F260" />
      <circle cx="25" cy="100" r="11" fill="#00F260" opacity="0.3">
        <animate attributeName="r" values="6;14;6" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="575" cy="35" r="6" fill="#1B93A4" />
      {/* Truck dot */}
      <circle cx="320" cy="62" r="7" fill="#00F260">
        <animate attributeName="cx" values="120;320;480;320;120" dur="8s" repeatCount="indefinite" />
        <animate attributeName="cy" values="55;62;48;62;55" dur="8s" repeatCount="indefinite" />
      </circle>
    </svg>
    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-xs">
      <div>
        <div className="text-[#475569] text-[10px] uppercase tracking-widest">{t.miniMockup.eta}</div>
        <div className="font-mono text-white">{t.miniMockup.etaTime}</div>
      </div>
      <div>
        <div className="text-[#475569] text-[10px] uppercase tracking-widest">CLIMA</div>
        <div className="font-mono text-white flex items-center gap-1"><Cloud className="w-3 h-3" /> {t.miniMockup.weather}</div>
      </div>
      <div>
        <div className="text-[#475569] text-[10px] uppercase tracking-widest">VEL</div>
        <div className="font-mono text-white">82 km/h</div>
      </div>
    </div>
  </div>
);

const InspectionVisual = ({ t }) => {
  const m = t.inspectionMock;
  const angles = [
    { label: m.angles[0], captured: true },
    { label: m.angles[1], captured: true },
    { label: m.angles[2], captured: true },
    { label: m.angles[3], scanning: true },
  ];
  return (
    <div className="mt-6 relative">
      {/* 4-angle photo grid */}
      <div className="grid grid-cols-4 gap-1.5 mb-2">
        {angles.map((a, i) => (
          <div key={i} className="aspect-[4/3] ag-glass rounded-md border border-[#00F260]/15 relative overflow-hidden flex items-center justify-center">
            {/* Viewfinder corners */}
            <span className="absolute top-1 left-1 w-1.5 h-1.5 border-t border-l border-[#00F260]/70" />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 border-t border-r border-[#00F260]/70" />
            <span className="absolute bottom-1 left-1 w-1.5 h-1.5 border-b border-l border-[#00F260]/70" />
            <span className="absolute bottom-1 right-1 w-1.5 h-1.5 border-b border-r border-[#00F260]/70" />
            {/* Car silhouette */}
            <Truck className="w-5 h-5 text-white/30" strokeWidth={1.4} />
            {/* Capture status */}
            {a.captured && (
              <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-[#00F260] flex items-center justify-center">
                <CheckCircle2 className="w-2.5 h-2.5 text-[#060B14]" strokeWidth={3} />
              </span>
            )}
            {a.scanning && (
              <>
                <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-[#00F260]/30 ag-pulse flex items-center justify-center">
                  <Camera className="w-2 h-2 text-[#00F260]" strokeWidth={2.5} />
                </span>
                <div className="absolute inset-x-0 h-[1.5px] bg-[#00F260] shadow-[0_0_8px_#00F260]" style={{ animation: 'ag-scan 1.6s ease-in-out infinite' }} />
              </>
            )}
            {/* angle label */}
            <span className="absolute bottom-0.5 left-0 right-0 text-[7px] font-mono text-[#00F260] tracking-[0.18em] text-center">{a.label}</span>
          </div>
        ))}
      </div>

      {/* Report badge */}
      <div className="flex items-center justify-between gap-2 ag-glass rounded-md px-2.5 py-2 border border-white/5">
        <div className="flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-[#00F260]" />
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#94A3B8]">{m.report}</span>
          <span className="text-[10px] text-white">{m.reportData}</span>
        </div>
        <span className="text-[9px] font-mono text-[#00F260] flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-[#00F260] animate-pulse" />
          {m.ready}
        </span>
      </div>

      {/* Damage detection panel */}
      <div className="mt-2 ag-glass rounded-md border border-white/5 px-2.5 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#475569]">{m.status}</span>
          <span className="text-[9px] font-mono text-[#00F260]">{m.statusOk}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {m.areas.map((it, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${it.ok ? 'bg-[#00F260]' : 'bg-amber-400 ag-blink'}`} />
              <span className="text-[10px] text-[#94A3B8] truncate">{it.area}</span>
              {!it.ok && <span className="text-[8px] font-mono text-amber-400 uppercase tracking-widest">!</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Inspection timeline */}
      <div className="mt-2 ag-glass rounded-md border border-white/5 px-2.5 py-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#475569]">{m.timeline}</span>
          <span className="text-[9px] font-mono text-[#94A3B8]">{m.now}</span>
        </div>
        <div className="space-y-1">
          {m.stages.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${it.done ? 'bg-[#00F260]' : 'bg-white/20 ag-blink'}`} />
              <span className="text-[10px] text-[#94A3B8] flex-1">{it.stage}</span>
              <span className={`text-[10px] font-mono ${it.done ? 'text-white' : 'text-[#475569]'}`}>{it.time}</span>
              {it.done ? (
                <CheckCircle2 className="w-3 h-3 text-[#00F260]" strokeWidth={2.5} />
              ) : (
                <Clock className="w-3 h-3 text-[#475569]" strokeWidth={2} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PinVisual = ({ t }) => {
  const m = t.pinMock;
  return (
    <>
      <div className="mt-6 flex items-center gap-2">
        {['8', '#', '4', '2'].map((n, i) => (
          <div key={i} className="flex-1 aspect-square ag-glass rounded-xl border border-[#00F260]/20 flex items-center justify-center font-mono text-2xl md:text-3xl font-bold text-[#00F260]">
            {n}
          </div>
        ))}
        <Lock className="w-5 h-5 text-[#00F260] ml-1" />
      </div>

      {/* Verification status */}
      <div className="mt-3 ag-glass rounded-md border border-[#00F260]/25 bg-[#00F260]/5 px-3 py-2 flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-full bg-[#00F260] flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4 text-[#060B14]" strokeWidth={3} />
        </span>
        <div className="flex-1 leading-tight">
          <div className="text-[11px] font-semibold text-white">{m.verified}</div>
          <div className="text-[10px] text-[#94A3B8] font-mono">{m.verifiedTime}</div>
        </div>
        <span className="text-[9px] font-mono text-[#00F260] tracking-widest">{m.ok}</span>
      </div>

      {/* Receiver info */}
      <div className="mt-2 ag-glass rounded-md border border-white/5 px-3 py-2 flex items-center gap-2.5">
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00F260] to-[#1B93A4] flex items-center justify-center shrink-0">
          <User className="w-3.5 h-3.5 text-[#060B14]" strokeWidth={2.5} />
        </span>
        <div className="flex-1 leading-tight">
          <div className="text-[11px] font-semibold text-white">{m.receiver}</div>
          <div className="text-[10px] text-[#94A3B8] font-mono">{m.receiverDoc}</div>
        </div>
        <ShieldCheck className="w-4 h-4 text-[#00F260]" />
      </div>
    </>
  );
};

const AlertsVisual = ({ language }) => {
  const items = language === 'es'
    ? [
        { t: 'Recogida confirmada', s: 'hace 2 min' },
        { t: 'En ruta — A2 km 85', s: 'hace 12 min' },
        { t: 'Llegada estimada 14:30', s: 'hace 28 min' }
      ]
    : [
        { t: 'Pickup confirmed', s: '2 min ago' },
        { t: 'On route — A2 km 85', s: '12 min ago' },
        { t: 'ETA 14:30', s: '28 min ago' }
      ];
  return (
    <div className="space-y-2.5">
      {items.map((it, i) => (
        <div key={i} className="ag-glass rounded-xl p-3.5 flex items-center gap-3 border border-white/5">
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#00F260]/15 border border-[#00F260]/30 flex items-center justify-center">
            <BellRing className="w-4 h-4 text-[#00F260]" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold">{it.t}</div>
            <div className="text-[10px] text-[#475569] mt-0.5 font-mono uppercase tracking-widest">{it.s}</div>
          </div>
          <Clock className="w-3.5 h-3.5 text-[#475569]" />
        </div>
      ))}
    </div>
  );
};

const ServiceRow = ({ index, title, desc, to, addReveal }) => {
  const [hovered, setHovered] = useState(false);
  const isInternalRoute = to && to.startsWith('/');
  const content = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group relative border-b border-white/10 py-7 md:py-10 transition-colors"
    >
      <div className={`absolute inset-0 -z-10 transition-opacity ${hovered ? 'opacity-100' : 'opacity-0'}`}
           style={{ background: 'radial-gradient(circle at 30% 50%, rgba(0,242,96,0.08), transparent 60%)' }} />
      <div className="grid md:grid-cols-12 items-center gap-4 md:gap-8">
        <div className="md:col-span-1 font-mono text-xs text-[#475569] tracking-widest">0{index + 1}</div>
        <h3 className={`md:col-span-7 font-display text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight transition-colors ${hovered ? 'text-[#00F260]' : 'text-white'}`}>
          {title}
        </h3>
        <p className="md:col-span-3 text-sm text-[#94A3B8]">{desc}</p>
        <div className="md:col-span-1 flex justify-end">
          <span className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${hovered ? 'bg-[#00F260] text-[#060B14] border-[#00F260] scale-110' : 'border-white/15 text-white'}`}>
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </div>
  );
  const commonProps = {
    ref: addReveal,
    className: 'ag-reveal block cursor-pointer',
    'data-testid': `service-row-${index}`
  };
  if (isInternalRoute) {
    return <Link to={to} {...commonProps}>{content}</Link>;
  }
  return <a href={to} {...commonProps}>{content}</a>;
};

/* Operating Margin — B2B premium teaser with gating form */
const FloatingField = ({ icon: Icon, label, name, value, onChange, placeholder, type = 'text', required, testId }) => (
  <div className="group relative flex items-center gap-3 border-b border-white/10 hover:border-[#00F260]/40 focus-within:border-[#00F260] transition-colors py-2">
    <Icon className="w-4 h-4 text-[#475569] group-focus-within:text-[#00F260] shrink-0 transition-colors" />
    <div className="flex-1 min-w-0">
      <label className="block text-[9px] uppercase tracking-[0.22em] text-[#475569] font-mono">{label}</label>
      <input
        className="w-full bg-transparent border-0 outline-none text-sm text-white placeholder:text-[#475569] py-1"
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
      />
    </div>
  </div>
);


/* Fleet Save — simplified teaser on home (driving traffic to /flotas landing) */
const OperatingMarginSection = ({ t, addReveal, language }) => {
  const m = t.operatingMargin;
  return (
    <section id="operating-margin" className="relative pt-12 md:pt-16 pb-16 md:pb-24 px-5 md:px-10" data-testid="operating-margin-section">
      <div className="max-w-7xl mx-auto">
        <div ref={addReveal} className="ag-reveal relative rounded-[28px] overflow-hidden border border-[#00F260]/20" style={{ background: 'linear-gradient(180deg, #030710 0%, #060B14 100%)' }}>
          <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#00F260]/12 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#1B93A4]/12 blur-3xl pointer-events-none" />
          <div className="absolute inset-0 ag-grid-bg opacity-[0.4] pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00F260] to-transparent" />

          <div className="relative grid lg:grid-cols-12 gap-8 md:gap-10 p-7 md:p-12 lg:p-16 items-center">
            {/* Decorative slash divider vertical (cyan/green) on large screens */}
            <div className="hidden lg:block absolute top-10 bottom-10 left-[58%] w-px bg-gradient-to-b from-transparent via-[#1B93A4]/30 to-transparent pointer-events-none" />

            {/* LEFT: Pitch */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00F260]/10 border border-[#00F260]/30 rounded-full text-[10px] tracking-[0.25em] font-mono text-[#00F260] mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00F260] ag-pulse" />
                {m.tag}
              </div>

              {/* Hero type block with extras */}
              <div className="relative">
                {/* Asterisk mark */}
                <span aria-hidden className="hidden md:block absolute -left-10 top-2 font-display text-[40px] text-[#1B93A4] font-extrabold leading-none select-none">*</span>
                <h2 className="font-display text-5xl md:text-7xl lg:text-[92px] font-extrabold leading-[0.9] tracking-tight">
                  <span className="block text-white">
                    {m.homeHeadline1}
                  </span>
                  <span className="block relative w-fit">
                    <span className="ag-shine-text">{m.homeHeadline2}</span>
                    {/* Underline swash */}
                    <svg aria-hidden viewBox="0 0 340 14" className="absolute -bottom-2 left-0 w-full h-3 pointer-events-none" preserveAspectRatio="none">
                      <path d="M2 8 Q 90 2, 180 7 T 338 6" stroke="#00F260" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.55" />
                    </svg>
                  </span>
                </h2>
              </div>

              <div className="mt-7 inline-flex items-center gap-2.5 font-mono text-[11px] tracking-[0.25em] uppercase">
                <span className="h-px w-8 bg-[#00F260]/40" />
                <span className="text-[#00F260] font-semibold">{m.name}</span>
                <span className="text-[#1B93A4]">·</span>
                <span className="text-[#1B93A4]">{language === 'es' ? 'PROGRAMA B2B' : 'B2B PROGRAM'}</span>
              </div>

              <p className="mt-5 text-base md:text-lg text-[#94A3B8] max-w-xl leading-relaxed">{m.description}</p>

              {/* Mini-metrics row (3 chips: green, green, cyan) */}
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00F260]/30 bg-[#00F260]/5 text-xs text-[#CBD5E1]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00F260]" />
                  −10% <span className="text-[#475569]">{language === 'es' ? 'por traslado' : 'per transfer'}</span>
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00F260]/30 bg-[#00F260]/5 text-xs text-[#CBD5E1]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00F260]" />
                  {language === 'es' ? 'Factura única mensual' : 'Single monthly invoice'}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1B93A4]/40 bg-[#1B93A4]/10 text-xs text-[#CBD5E1]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1B93A4]" />
                  {language === 'es' ? 'Gestor dedicado 24/7' : 'Dedicated 24/7 manager'}
                </span>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/flotas" className="group inline-flex items-center gap-2 bg-[#00F260] text-[#060B14] font-semibold px-6 py-3.5 rounded-full hover:bg-white transition-colors" data-testid="om-cta-landing">
                  {language === 'es' ? 'Ver Fleet Save' : 'See Fleet Save'}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link to="/flotas#fs-calc" className="group inline-flex items-center gap-2 border border-[#1B93A4]/40 text-[#1B93A4] px-6 py-3.5 rounded-full hover:border-[#1B93A4] hover:bg-[#1B93A4]/10 transition-colors" data-testid="om-cta-calc">
                  <TrendingUp className="w-4 h-4" />
                  {language === 'es' ? 'Calcular mi ahorro' : 'Calculate my savings'}
                </Link>
              </div>
            </div>

            {/* RIGHT: Big -10% + real-money sub-stat + mini bar-chart (cyan accent) */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-[360px]">
                <div className="absolute -inset-8 rounded-full bg-[#00F260]/15 blur-3xl pointer-events-none" />
                <div className="absolute -inset-4 rounded-full bg-[#1B93A4]/10 blur-3xl pointer-events-none" />
                <div className="relative ag-glass-strong rounded-3xl p-7 md:p-8 border border-[#00F260]/30 text-center ag-animate-float overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#1B93A4] via-[#00F260] to-[#1B93A4]" />
                  <div className="absolute -top-20 -right-10 w-40 h-40 rounded-full bg-[#00F260]/15 blur-3xl pointer-events-none" />
                  {/* Primary metric */}
                  <div className="relative">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-[#00F260]" strokeWidth={3} />
                      <span className="font-mono text-[10px] tracking-[0.3em] text-[#00F260]">{m.homeWidgetLabel}</span>
                    </div>
                    <div className="font-mono text-[11px] md:text-xs tracking-[0.28em] text-white/90 mb-1">{m.homeWidgetUpTo}</div>
                    <div className="font-display text-[72px] md:text-[84px] font-extrabold leading-none ag-shine-text">{m.homeWidgetBig}</div>
                    <div className="mt-3 font-mono text-[9px] tracking-[0.25em] text-[#94A3B8] leading-snug">{m.homeWidgetUnit}</div>
                  </div>

                  {/* Tiny ascending bar chart (cyan) */}
                  <div className="mt-5 flex items-end justify-center gap-1 h-10">
                    {[22, 35, 28, 46, 38, 58, 52, 72, 68, 86].map((h, i) => (
                      <span key={i} className="w-[4px] rounded-full" style={{ height: `${h}%`, background: i > 6 ? '#00F260' : '#1B93A4', opacity: 0.4 + i * 0.06 }} />
                    ))}
                  </div>

                  {/* Money sub-stat */}
                  <div className="mt-4 pt-5 border-t border-white/10 relative">
                    <div className="font-mono text-[9px] tracking-[0.3em] text-[#475569] mb-1.5">{m.homeWidgetMoneyLabel}</div>
                    <div className="flex items-baseline justify-center gap-1.5">
                      <span className="font-display text-2xl md:text-3xl font-extrabold text-white" data-testid="om-widget-money">{m.homeWidgetMoney}</span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#00F260]/15 border border-[#00F260]/40">
                        <span className="inline-block w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-b-[5px] border-b-[#00F260]" />
                        <span className="font-mono text-[9px] text-[#00F260] font-semibold tracking-wider">YTD</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


/* Quick Access Strip — featured services right under the hero/stats */
const QuickAccessStrip = ({ t, addReveal }) => {
  const q = t.quickAccess;
  return (
    <section className="relative pt-10 md:pt-14 pb-2 md:pb-4 px-5 md:px-10" data-testid="quick-access-strip">
      <div className="max-w-7xl mx-auto">
        <div ref={addReveal} className="ag-reveal mb-7 md:mb-9 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] text-[#00F260] mb-3 flex items-center gap-3">
              <span className="h-px w-8 bg-[#00F260]/40" />
              {q.tagline}
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[0.95] tracking-tight">{q.title}</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {q.cards.map((card, i) => {
            const isFeatured = card.featured;
            const isExternal = card.to.startsWith('/');
            const accent = isFeatured ? '#ff5b5b' : '#00F260';
            const cardContent = (
              <>
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl pointer-events-none" style={{ background: isFeatured ? 'rgba(255,91,91,0.18)' : 'rgba(0,242,96,0.12)' }} />
                {isFeatured && (
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#ff5b5b] via-[#ff8b5b] to-[#ff5b5b]" />
                )}
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-5">
                    <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded text-[9px] md:text-[10px] font-mono tracking-[0.22em]" style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}66` }}>
                      {isFeatured && <span className="inline-flex h-1.5 w-1.5 rounded-full ag-pulse" style={{ background: accent }} />}
                      {card.tag}
                    </span>
                    {card.discount ? (
                      <div className="flex items-center gap-2">
                        <div className="hidden sm:flex w-7 h-7 rounded-md bg-[#00F260]/12 border border-[#00F260]/40 items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-[#00F260]" />
                        </div>
                        <div className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00F260] text-[#060B14]">
                          <TrendingUp className="w-3.5 h-3.5" strokeWidth={3} />
                          <span className="font-display text-sm font-extrabold leading-none">{card.discount}</span>
                          <span className="font-mono text-[8px] tracking-[0.2em] font-semibold leading-none hidden sm:inline">{card.discountLabel}</span>
                        </div>
                      </div>
                    ) : (
                      <span className="font-mono text-[10px] tracking-widest text-[#475569]">0{i + 1}</span>
                    )}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-extrabold leading-[0.95] tracking-tight">
                    <span className="block text-white">{card.title}</span>
                    <span className="block" style={{ color: accent }}>{card.highlight}</span>
                  </h3>
                  <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{card.desc}</p>
                  <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase transition-colors group-hover:gap-3" style={{ color: accent }}>
                    {card.cta}
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </>
            );
            const className = `group relative ag-card ag-hover-lift block p-6 md:p-8 overflow-hidden min-h-[260px] transition-colors ${isFeatured ? 'border-[#ff5b5b]/30' : ''}`;
            return isExternal ? (
              <Link key={i} to={card.to} className={className} data-testid={`quick-access-card-${i}`}>
                {cardContent}
              </Link>
            ) : (
              <a key={i} href={card.to} className={className} data-testid={`quick-access-card-${i}`}>
                {cardContent}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};


/* Clients trust marquee — wordmarks of real customers */
const ClientsMarquee = ({ t }) => {
  const clients = [
    { name: 'Clidrive', src: '/logos/clidrive.svg', type: 'img' },
    { name: 'Omocom', src: '/logos/omocom.png', type: 'img' },
    { name: 'Amovens', src: '/logos/amovens.svg', type: 'img' },
    { name: 'Ayvens', src: '/logos/ayvens.svg', type: 'img' },
    { name: 'Prodalim', src: '/logos/prodalim.svg', type: 'img' },
    { name: 'RDT Ingenieros', type: 'text', style: 'font-display font-bold tracking-tight' },
    { name: 'MeRenting', type: 'text', style: 'font-display font-bold tracking-tight' },
  ];
  // Duplicate twice for seamless loop
  const loop = [...clients, ...clients];
  return (
    <section className="relative z-10 pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden" data-testid="clients-strip">
      <div className="max-w-7xl mx-auto px-5 md:px-10 mb-7 flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-2.5">
          <span className="h-px w-10 bg-[#00F260]/40" />
          <span className="font-mono text-xs tracking-[0.3em] text-[#00F260]">{t.clients.tagline}</span>
          <span className="h-px w-10 bg-[#00F260]/40" />
        </div>
        <p className="mt-2 text-xs text-[#94A3B8] md:text-sm">{t.clients.subtitle}</p>
      </div>

      {/* Marquee */}
      <div className="relative">
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 z-10 bg-gradient-to-r from-[#060B14] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 z-10 bg-gradient-to-l from-[#060B14] to-transparent" />

        <div className="flex ag-marquee-track gap-12 md:gap-20 whitespace-nowrap items-center">
          {loop.map((c, i) => (
            <div
              key={i}
              className="shrink-0 flex items-center"
              data-testid={`client-logo-${i}`}
            >
              {c.type === 'img' ? (
                <div className="rounded-lg bg-white/[0.1] px-3 py-2 ring-1 ring-white/10 [forced-color-adjust:none]">
                  <img
                    src={c.src}
                    alt={c.name}
                    className="h-7 w-auto object-contain transition-opacity duration-300 hover:opacity-100 md:h-9 [forced-color-adjust:none]"
                    style={{ filter: 'brightness(0) invert(1)', opacity: 0.9 }}
                  />
                </div>
              ) : (
                <span className={`text-2xl md:text-3xl text-white/45 hover:text-white/90 transition-colors ${c.style}`}>
                  {c.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* Client Dashboard Mockup — premium showcase */
const ClientDashboardMockup = ({ t, language }) => {
  const d = t.dashboard;
  const services = [
    { name: d.svc1, Icon: Radar, status: 'on' },
    { name: d.svc2, Icon: ScanLine, status: 'on' },
    { name: d.svc3, Icon: Lock, status: 'on' },
    { name: d.svc4, Icon: BellRing, status: 'on' }
  ];
  return (
    <div className="ag-glass-strong rounded-2xl p-4 w-full max-w-[400px] relative overflow-hidden" data-testid="client-dashboard-mockup">
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#00F260]/10 blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-center justify-between mb-4 relative z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F260] to-[#1B93A4] flex items-center justify-center text-[#060B14]">
            <User className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-sm font-semibold leading-none">{d.greeting}</div>
            <div className="font-mono text-[9px] tracking-widest text-[#475569] mt-1">{d.client}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Bell className="w-3.5 h-3.5 text-[#94A3B8]" />
          <span className="font-mono text-[9px] tracking-widest text-[#00F260]">{d.tag}</span>
        </div>
      </div>

      {/* Active shipment card */}
      <div className="rounded-xl border border-[#00F260]/25 bg-[#00F260]/5 p-3.5 mb-3 relative z-10">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Package className="w-3.5 h-3.5 text-[#00F260]" />
            <span className="text-[10px] uppercase tracking-widest font-mono text-[#94A3B8]">{d.activeShipment}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[9px] font-mono text-[#00F260]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#00F260] animate-pulse" />
            {t.miniMockup.live}
          </span>
        </div>
        <div className="font-display text-base font-bold mb-3">{d.shipmentRoute}</div>
        {/* Progress bar */}
        <div className="flex items-center gap-1.5">
          {d.stages.map((stage, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center flex-1">
                <div className={`w-3 h-3 rounded-full border-2 ${i <= d.stageActive ? 'bg-[#00F260] border-[#00F260]' : 'border-white/20'} ${i === d.stageActive ? 'shadow-[0_0_10px_#00F260]' : ''}`} />
                <span className={`mt-1.5 text-[9px] font-mono tracking-wider ${i <= d.stageActive ? 'text-white' : 'text-[#475569]'}`}>{stage}</span>
              </div>
              {i < d.stages.length - 1 && (
                <div className={`flex-1 h-px mt-[-14px] ${i < d.stageActive ? 'bg-[#00F260]' : 'bg-white/10'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Services grid */}
      <div className="mb-3 relative z-10">
        <div className="font-mono text-[9px] tracking-widest text-[#475569] mb-2">{d.services}</div>
        <div className="grid grid-cols-2 gap-1.5">
          {services.map((s, i) => (
            <div key={i} className="flex items-center gap-2 ag-glass rounded-lg px-2.5 py-2 border border-white/5">
              <s.Icon className="w-3.5 h-3.5 text-[#00F260]" />
              <span className="text-[11px] font-medium flex-1 truncate">{s.name}</span>
              <span className="h-1.5 w-1.5 rounded-full bg-[#00F260] ag-blink" />
            </div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-1.5 relative z-10">
        <div className="ag-glass rounded-lg px-3 py-2 border border-white/5">
          <div className="font-mono text-[9px] tracking-widest text-[#475569]">{d.stat1}</div>
          <div className="text-[12px] font-semibold mt-0.5">{d.stat1v}</div>
        </div>
        <div className="ag-glass rounded-lg px-3 py-2 border border-white/5">
          <div className="font-mono text-[9px] tracking-widest text-[#475569]">{d.stat2}</div>
          <div className="text-[12px] font-semibold mt-0.5 text-[#00F260]">{d.stat2v}</div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
